  import React, { useEffect, useRef, useState } from "react";
  import gsap from "gsap";
  import { ScrollTrigger } from "gsap/ScrollTrigger";
  import { Play } from "lucide-react";

  gsap.registerPlugin(ScrollTrigger);

  const HeroSection = ({
    title,
    description,
    imageSrc, // Manual override if you don't want the YT thumbnail
    youtubeUrl = "https://www.youtube.com/watch?v=9AL6Km6rcX0",
    subtitle = null,
  }) => {
    const sectionRef = useRef(null);
    const [showVideo, setShowVideo] = useState(false);

    // Helper function to get YouTube ID from any link format
    const getYouTubeID = (url) => {
      const regExp =
        /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url?.match(regExp);
      return match && match[2].length === 11 ? match[2] : null;
    };

    const videoId = getYouTubeID(youtubeUrl);

    // Dynamic Thumbnail URLs
    const ytMaxRes = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    const ytFallback = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

    useEffect(() => {
      const context = gsap.context(() => {
        const textElements = gsap.utils.toArray(
          sectionRef.current.querySelectorAll(".animated-text-content"),
        );

        textElements.forEach((element, index) => {
          gsap.set(element, {
            y: 25,
            opacity: 0,
            clipPath: "inset(100% 0 0% 0)",
          });
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: element.closest("div"),
              start: "top 85%",
              end: "center 60%",
              scrub: 1,
            },
          });
          tl.to(element, {
            y: 0,
            opacity: 1,
            clipPath: "inset(0% 0 0% 0)",
            duration: 1,
            ease: "power2.out",
            delay: index * 0.1,
          });
        });
      }, sectionRef);

      return () => context.revert();
    }, []);

    return (
      <div
        ref={sectionRef}
        className="flex flex-col gap-[25px] sm:gap-[37px] lg:gap-[50px] justify-start items-center w-full mt-[40px] md:mt-[80px] 2xl:mt-[132px] px-[32px]"
      >
        <div className="flex flex-col gap-[25px] sm:gap-[37px] lg:gap-[50px] justify-start items-center w-full max-w-[1612px]">
          {/* Text Content */}
          <div className="flex flex-col gap-[10px] sm:gap-[15px] lg:gap-[20px] justify-start items-center w-full">
            {subtitle && (
              <div className="about-subtitle flex justify-center items-center gap-[20px] mb-2">
                <span className="animated-text-content text-sm font-semibold uppercase tracking-widest text-black">
                  {subtitle}
                </span>
                <div className="about-divider-line h-[2px] w-[50px] bg-black"></div>
              </div>
            )}
            <h1 className="animated-text-content text-[24px] sm:text-[45px] lg:text-[60px] font-bold leading-tight text-center font-[Sora]">
              {title || "Transforming Waste into Renewable Energy"}
            </h1>
            <p className="animated-text-content text-[12px] sm:text-[18px] lg:text-[24px] font-normal leading-relaxed text-center max-w-[1000px] font-[Sora]">
              {description ||
                "Bio gas is a renewable energy source produced through anaerobic digestion of organic waste such as agricultural residues, food waste, and animal manure."}
            </p>
          </div>

          {/* Media Container */}
          <div className="relative w-full bg-neutral-200 rounded-[15px] sm:rounded-[22px] lg:rounded-[30px] h-[343px] sm:h-[514px] lg:h-[686px] overflow-hidden group shadow-xl">
            {!showVideo ? (
              /* PHASE 1: DYNAMIC THUMBNAIL IMAGE */
              <div
                className="relative w-full h-full cursor-pointer"
                onClick={() => setShowVideo(true)}
              >
                <img
                  // Use imageSrc if provided as prop, otherwise use YouTube dynamic thumbnail
                  src={imageSrc || ytMaxRes}
                  alt="Video Thumbnail"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = ytFallback;
                  }}
                />
  
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-all duration-300">
                  <div className="w-[40px] h-[40px] sm:w-[70px] sm:h-[70px] bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/50 transform group-hover:scale-110 transition-transform">
                    <Play size={30} fill="black" className="ml-1" />
                  </div>
                </div>
              </div>
            ) : (
              /* PHASE 2: YOUTUBE IFRAME */
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            )}
          </div>
        </div>
      </div>
    );
  };

  export default HeroSection;
